import { useMemo } from 'react';
import { keyBy } from 'lodash-es';
import type { QueryKey, UseQueryOptions } from '@soundxyz/graphql-react-query/reactQuery';
import { useQueries, useQuery } from '@soundxyz/graphql-react-query/reactQuery';
import { MINT_GAS_LIMIT_MULTIPLIER } from '@soundxyz/sdk/utils/constants';
import { assertIsHex, scaleAmount } from '@soundxyz/sdk/utils/helpers';
import type { SuperMinterSchedule } from '@soundxyz/tiered-sdk-viem';
import { SOUND_EDITION_V2_ABI } from '@soundxyz/tiered-sdk-viem/abi/sound-edition-v2';
import { SUPER_MINTER_ABI } from '@soundxyz/tiered-sdk-viem/abi/super-minter';
import type { GetEditionContractInfoReturnType } from '@soundxyz/tiered-sdk-viem/actions/public/getEditionContractInfo';
import type { Maybe } from '@soundxyz/utils';
import { getFromList, getManyFromList, LazyPromise } from '@soundxyz/utils';
import { assertAddress } from '@soundxyz/utils/address';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { getSoundContractAddressesConstants } from '@components/Dashboard/upload-new/constants';
import { useAuth } from '@context/auth/base';
import type { ContractType, FragmentType } from '@graphql/reactQuery';
import { EditionCollectableSchedulesInfoFragmentDoc, getFragment } from '@graphql/reactQuery';
import { GraphQLReactQuery } from '@graphql/reactQuery';
import { sdkSoundAPI } from '@hooks/useSoundSdk';
import { useTieredSdk } from '@hooks/useTieredSdk';
import { bigIntToNumber } from '@utils/bigNumberHelpers';
import { expectNormalGas, SDK_KEY } from './helpers';

export type SuperMinterScheduleWithTier = SuperMinterSchedule & {
  tierInfo: NonNullable<GetEditionContractInfoReturnType['tierInfo'][number]>;
};

export function useTieredEditionMintSchedules({
  editionAddress,
  chainId,
  auctionContractType,
}: {
  editionAddress: string | undefined;
  chainId: number | undefined;
  auctionContractType: ContractType | null;
}) {
  const { tieredProvider } = useTieredSdk();

  const enabled =
    editionAddress != null && chainId != null && auctionContractType === 'TIERED_EDITION';

  return useQuery({
    queryKey: [SDK_KEY, 'tiered-schedules', editionAddress, chainId],
    async queryFn(): Promise<SuperMinterScheduleWithTier[]> {
      if (!enabled) return [];

      assertAddress(editionAddress);

      const provider = tieredProvider(chainId);

      const [tierData, schedules] = await Promise.all([
        GraphQLReactQuery.fetchQuery({
          queryKey: tieredEditionInfoQueryKey({ editionAddress, chainId }),
          queryFn() {
            return tieredEditionInfoQueryFn({
              editionAddress,
              chainId,
              tieredProvider,
            });
          },
          staleTime: 0,
        }).then(data => keyBy(data.tierInfo, v => v.tier)),
        provider
          .getMintingSchedules({
            editionAddress,
          })
          .then(data => data.schedules.map(v => v)),
      ]);

      return getManyFromList(
        schedules.map(v => v),
        schedule => {
          const tierInfo = tierData[schedule.tier];

          if (!tierInfo) return null;

          return {
            ...schedule,
            tierInfo,
          };
        },
      );
    },
    enabled,
    staleTime: 0,
    select(tieredSchedules) {
      const now = Math.floor(Date.now() / 1000);

      return {
        tieredSchedules,
        activeTieredSchedules: tieredSchedules.filter(
          schedule => schedule.startTime <= now && schedule.endTime > now && !schedule.paused,
        ),
      };
    },
  });
}

export function useTieredEditionInfo({
  editionAddress,
  chainId,
  auctionContractType,
}: {
  editionAddress: string | undefined;
  chainId: number | undefined;
  auctionContractType: ContractType | null;
}) {
  const { tieredProvider } = useTieredSdk();

  const enabled =
    editionAddress != null && chainId != null && auctionContractType === 'TIERED_EDITION';

  return useQuery({
    queryKey: enabled ? tieredEditionInfoQueryKey({ editionAddress, chainId }) : undefined,
    queryFn() {
      if (!enabled) return;

      return tieredEditionInfoQueryFn({
        editionAddress,
        chainId,
        tieredProvider,
      });
    },
    enabled,
    staleTime: 0,
  });
}

export function useEstimateTieredMintGas({
  mintSchedule,
  chainId,
  quantity,
  editionAddress,
  totalValue,
}: {
  mintSchedule: SuperMinterScheduleWithTier;
  chainId: number;
  quantity: number;
  editionAddress: string;
  totalValue: Maybe<bigint>;
}) {
  const { authUserAddress } = useAuth();
  const { tieredProvider } = useTieredSdk();

  const result = useQuery({
    staleTime: Infinity,
    cacheTime: Infinity,
    queryKey: [
      SDK_KEY,
      'tiered-mint-estimate',
      editionAddress,
      quantity,
      authUserAddress,
      chainId,
      mintSchedule.tier,
      mintSchedule.scheduleNum,
    ],
    async queryFn() {
      if (!authUserAddress || !totalValue) return null;

      assertAddress(editionAddress);

      const allowlistProof =
        mintSchedule.mode === 'VERIFY_MERKLE'
          ? await sdkSoundAPI.merkleProof({
              merkleRoot: mintSchedule.merkleRoot,
              userAddress: authUserAddress,
            })
          : null;

      if (mintSchedule.mode === 'VERIFY_MERKLE') {
        if (!allowlistProof) throw Error('Not eligible!');
      }

      const gasEstimate = await tieredProvider(chainId).estimateGasMintTieredEdition({
        account: authUserAddress,
        edition: editionAddress,
        mintParams:
          mintSchedule.mode === 'VERIFY_MERKLE' && allowlistProof
            ? {
                mode: 'VERIFY_MERKLE',
                allowlisted: authUserAddress,
                allowlistProof,
              }
            : {
                mode: 'DEFAULT',
              },
        quantity,
        scheduleNum: mintSchedule.scheduleNum,
        tier: mintSchedule.tier,
        to: authUserAddress,
        value: totalValue,
      });

      const gasLimit = expectNormalGas({
        estimation: scaleAmount({
          amount: gasEstimate,
          multiplier: MINT_GAS_LIMIT_MULTIPLIER,
        }),
        editionAddress,
        chainId,
        quantity,
        loggingExtra: {
          mintSchedule,
        },
      });

      return {
        gasLimit,
      };
    },
    enabled: !!authUserAddress && !!quantity && !!totalValue,
  });

  return {
    ...result,
    isMintEstimationLoadingWithoutErrors: result.isInitialLoading && result.failureCount === 0,
  };
}

/**
 * @note this hook is used to check if the user has access to the mint schedule regardless of how many editions they have minted
 */
export function useHasTieredScheduleEligibility({
  mintSchedule,
  editionAddress,
  contractType,
}: {
  mintSchedule: Maybe<SuperMinterScheduleWithTier>;
  editionAddress: string;
  contractType: ContractType;
}) {
  const { authUserAddress } = useAuth();

  const scheduleAccessDropInfo = useMemo<AccessToDropScheduleInfo | null>(() => {
    if (!mintSchedule) return null;

    return getAccessToDropScheduleInfo(mintSchedule);
  }, [mintSchedule]);

  return useQuery(
    hasScheduleEligibility({
      editionAddress,
      mintSchedule,
      authUserAddress,
      scheduleAccessDropInfo,
      contractType,
    }),
  );
}

export function useHasAccessToTieredSchedule({
  mintSchedule,
  editionAddress,
  chainId,
}: {
  mintSchedule: Maybe<SuperMinterScheduleWithTier>;
  editionAddress: string;
  chainId: number;
}) {
  const { authUserAddress } = useAuth();
  const { tieredProvider } = useTieredSdk();

  const scheduleAccessDropInfo = useMemo<AccessToDropScheduleInfo | null>(() => {
    if (!mintSchedule) return null;

    return getAccessToDropScheduleInfo(mintSchedule);
  }, [mintSchedule]);

  return useQuery(
    hasAccessToTieredScheduleQuery({
      chainId,
      editionAddress,
      scheduleAccessDropInfo,
      authUserAddress,
      mintSchedule,
      tieredProvider,
    }),
  );
}

export function useTieredEditionEligibleQuantity<QueryData = bigint | null>(
  {
    mintSchedule,
    chainId,
    editionAddress,
  }: {
    mintSchedule: Maybe<SuperMinterScheduleWithTier>;
    chainId: number;
    editionAddress: string;
  },
  options?: Omit<
    UseQueryOptions<
      bigint | null,
      Error,
      QueryData,
      ReturnType<typeof tieredEligibleQuantityQueryKey>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  const { authUserAddress: userAddress } = useAuth();
  const { tieredProvider } = useTieredSdk();

  const getTieredEligibleQuantity = useStableCallback(
    async ({
      mintSchedule,
      userAddress,
      editionAddress,
    }: {
      mintSchedule: Maybe<SuperMinterScheduleWithTier>;
      userAddress: Maybe<string>;
      editionAddress: Maybe<string>;
    }) => {
      if (!mintSchedule || !userAddress || !editionAddress) return 0n;

      assertAddress(userAddress);
      assertAddress(editionAddress);

      const eligibleQuantity = await tieredProvider(chainId)
        .getMintEligibility({
          collectorAddress: userAddress,
          editionAddress,
          scheduleNum: mintSchedule.scheduleNum,
          tier: mintSchedule.tier,
          merkleProvider: {
            merkleProof: ({ merkleRoot, userAddress }) =>
              sdkSoundAPI.merkleProof({
                merkleRoot,
                userAddress,
              }),
          },
        })
        .then(v => BigInt(v.remainingEligibility));

      GraphQLReactQuery.setQueryData(
        tieredEligibleQuantityQueryKey({ mintSchedule, userAddress, editionAddress }),
        eligibleQuantity,
      );

      return eligibleQuantity;
    },
  );

  const result = useQuery({
    queryKey: tieredEligibleQuantityQueryKey({ mintSchedule, userAddress, editionAddress }),
    queryFn() {
      return getTieredEligibleQuantity({
        mintSchedule,
        userAddress,
        editionAddress,
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && !!mintSchedule && !!userAddress,
  });

  return {
    ...result,
    getTieredEligibleQuantity,
  };
}

export function useTieredMintPrice({
  editionAddress,
  mintSchedule,
  chainId,
  quantity,
}: {
  editionAddress: string;
  mintSchedule: Maybe<SuperMinterScheduleWithTier>;
  chainId: number;
  quantity: number;
}) {
  const { tieredProvider } = useTieredSdk();

  const { data: totalMintPriceWei } = useQuery({
    queryKey: [
      SDK_KEY,
      'tiered-mint-price',
      editionAddress,
      chainId,
      mintSchedule?.tier,
      mintSchedule?.scheduleNum,
      quantity,
    ],
    async queryFn() {
      if (!mintSchedule) return null;

      assertAddress(editionAddress);

      const { total, subTotal } = await tieredProvider(chainId).getTotalMintPriceAndFees({
        quantity,
        scheduleNum: mintSchedule.scheduleNum,
        tier: mintSchedule.tier,
        edition: editionAddress,
      });

      return { total, subTotal, fees: total - subTotal };
    },
    enabled: !!mintSchedule,
  });

  return {
    totalMintPriceWei,
  };
}

export function useTieredEditionMintScheduleWithEligibility({
  release,
}: {
  release: FragmentType<EditionCollectableSchedulesInfoFragmentDoc>;
}) {
  const { tieredProvider } = useTieredSdk();
  const { authUserAddress } = useAuth();

  const {
    chainId,
    contract: { contractType, contractAddress: editionAddress },
    auctionContractType,
  } = getFragment(EditionCollectableSchedulesInfoFragmentDoc, release);

  const { data: { tieredSchedules } = {} } = useTieredEditionMintSchedules({
    auctionContractType,
    chainId,
    editionAddress,
  });

  const queriesResult = useQueries({
    queries: (tieredSchedules || []).map(schedule => {
      return hasAccessToTieredScheduleQuery({
        editionAddress,
        scheduleAccessDropInfo: getAccessToDropScheduleInfo(schedule),
        authUserAddress,
        mintSchedule: schedule,
        chainId,
        tieredProvider,
      });
    }),
  });

  const scheduleAccessResults = useQueries({
    queries: (tieredSchedules || []).map(schedule => {
      return hasScheduleEligibility({
        editionAddress,
        scheduleAccessDropInfo: getAccessToDropScheduleInfo(schedule),
        contractType,
        authUserAddress,
        mintSchedule: schedule,
      });
    }),
  });

  const schedulesWithEligibility = useMemo(() => {
    return (tieredSchedules || []).map((schedule, index) => {
      return {
        schedule,
        hasSaleAccess: queriesResult[index]?.data,
        hasScheduleEligibility: scheduleAccessResults[index]?.data,
      };
    });
  }, [tieredSchedules, queriesResult, scheduleAccessResults]);

  return {
    scheduleAccessResults,
    schedulesWithEligibility,
  };
}

export function useTieredEditionCollectableSchedules({
  release,
}: {
  release: FragmentType<EditionCollectableSchedulesInfoFragmentDoc>;
}) {
  const {
    chainId,
    contract: { contractAddress: editionAddress },
    auctionContractType,
  } = getFragment(EditionCollectableSchedulesInfoFragmentDoc, release);

  const { data: { activeTieredSchedules } = {} } = useTieredEditionMintSchedules({
    editionAddress,
    chainId,
    auctionContractType,
  });

  const { authUserAddress } = useAuth();
  const { tieredProvider } = useTieredSdk();

  const rawActiveSchedules = useMemo(() => {
    return activeTieredSchedules ?? [];
  }, [activeTieredSchedules]);

  const queriesResult = useQueries({
    queries: rawActiveSchedules.map(schedule => {
      return hasAccessToTieredScheduleQuery({
        editionAddress,
        scheduleAccessDropInfo: getAccessToDropScheduleInfo(schedule),
        authUserAddress,
        mintSchedule: schedule,
        chainId,
        tieredProvider,
      });
    }),
  });

  const activeSchedulesWithEligibility = useMemo(() => {
    return rawActiveSchedules.map((schedule, index) => {
      return {
        schedule,
        hasSaleAccess: queriesResult[index]?.data,
      };
    });
  }, [queriesResult, rawActiveSchedules]);

  const firstCollectableSchedule = useMemo(() => {
    return getFromList(
      activeSchedulesWithEligibility,
      (result, index) => (result && rawActiveSchedules[index]) || null,
    );
  }, [activeSchedulesWithEligibility, rawActiveSchedules]);

  const firstCollectableScheduleWithSaleAccess = useMemo(() => {
    return getFromList(
      activeSchedulesWithEligibility,
      (result, index) => (result.hasSaleAccess && rawActiveSchedules[index]) || null,
    );
  }, [activeSchedulesWithEligibility, rawActiveSchedules]);

  return {
    queriesResult,
    activeSchedulesWithEligibility,
    firstCollectableSchedule,
    firstCollectableScheduleWithSaleAccess,
  };
}

function getAccessToDropScheduleInfo(
  mintSchedule: SuperMinterScheduleWithTier,
): AccessToDropScheduleInfo {
  return {
    scheduleStartTime: mintSchedule.startTime,
    scheduleEndTime: mintSchedule.endTime,
    scheduleMaxMintable: mintSchedule.maxMintable,
    scheduleMaxMintablePerAccount: mintSchedule.maxMintablePerAccount,
    scheduleMerkleInfo:
      mintSchedule.mode === 'VERIFY_MERKLE' ? { merkleRoot: mintSchedule.merkleRoot } : null,
    scheduleMintPaused: mintSchedule.paused,
    scheduleTotalMinted: mintSchedule.minted,
    scheduleRangeInfo:
      mintSchedule.mode === 'DEFAULT'
        ? {
            cutoffTime: mintSchedule.tierInfo.cutoffTime,
            maxMintableLower: mintSchedule.tierInfo.maxMintableLower,
            maxMintableUpper: mintSchedule.tierInfo.maxMintableUpper,
          }
        : null,
  };
}

function hasScheduleEligibility({
  editionAddress,
  mintSchedule,
  authUserAddress: authUserAddress,
  scheduleAccessDropInfo,
  contractType,
}: {
  editionAddress: string;
  mintSchedule: Maybe<SuperMinterScheduleWithTier>;
  authUserAddress: string | undefined;
  scheduleAccessDropInfo: Maybe<AccessToDropScheduleInfo>;
  contractType: ContractType;
}) {
  return {
    queryKey: [
      SDK_KEY,
      'tiered-has-schedule-access',
      editionAddress,
      authUserAddress,
      scheduleAccessDropInfo,
    ] as const,
    queryFn(): Promise<boolean> | boolean {
      if (contractType === 'ARTIST') return !!authUserAddress;

      if (!authUserAddress || !scheduleAccessDropInfo || !mintSchedule) return false;

      const hasMerkleAccess = async () => {
        if (!scheduleAccessDropInfo) return false;

        const { scheduleMerkleInfo } = scheduleAccessDropInfo;

        if (!scheduleMerkleInfo) return true;

        const proof = await sdkSoundAPI.merkleProof({
          merkleRoot: scheduleMerkleInfo.merkleRoot,
          userAddress: authUserAddress,
        });

        if (!proof?.length) {
          return false;
        } else {
          return true;
        }
      };
      return hasMerkleAccess();
    },
    enabled: !!mintSchedule && !!scheduleAccessDropInfo,
  };
}

interface AccessToDropScheduleInfo {
  scheduleStartTime: number;
  scheduleEndTime: number;
  scheduleMintPaused: boolean;
  scheduleTotalMinted: number;
  scheduleMaxMintable: number;
  scheduleMaxMintablePerAccount: number;

  scheduleRangeInfo: {
    cutoffTime: number;
    maxMintableUpper: number;
    maxMintableLower: number;
  } | null;
  scheduleMerkleInfo: {
    merkleRoot: string;
  } | null;
}

function hasAccessToTieredScheduleQuery({
  editionAddress,
  scheduleAccessDropInfo,
  chainId,
  authUserAddress: userAddress,
  mintSchedule,
  tieredProvider,
}: {
  editionAddress: string;
  chainId: number;
  scheduleAccessDropInfo: AccessToDropScheduleInfo | null;
  authUserAddress: string | undefined;
  tieredProvider: ReturnType<typeof useTieredSdk>['tieredProvider'];
  mintSchedule: Maybe<SuperMinterScheduleWithTier>;
}) {
  return {
    enabled: !!mintSchedule && !!scheduleAccessDropInfo,
    queryKey: [
      SDK_KEY,
      'tiered-has-schedule-access',
      editionAddress,
      userAddress,
      scheduleAccessDropInfo,
      chainId.toString(),
    ] as const,
    queryFn(): Promise<boolean> | boolean {
      if (mintSchedule?.tierInfo.mintConcluded) return false;

      if (!userAddress || !scheduleAccessDropInfo || !mintSchedule) return false;

      // Use react query orchestration to synchronize these calls within multiple calls
      const sharedPromises = GraphQLReactQuery.fetchQuery({
        queryKey: [
          'tiered-edition-info',
          editionAddress,
          userAddress,
          chainId.toString(),
          mintSchedule.tier,
        ],
        queryFn() {
          assertIsHex(editionAddress);

          const tierInfo = LazyPromise(() => {
            return tieredProvider(chainId).readContract({
              abi: SOUND_EDITION_V2_ABI,
              address: editionAddress,
              functionName: 'tierInfo',
              args: [mintSchedule.tier],
            });
          });

          const editionMaxMintablePromise = LazyPromise(() => {
            return tierInfo.then(value => value.maxMintable);
          });

          const editionTotalMintedPromise = LazyPromise(() => {
            return tierInfo.then(value => BigInt(value.minted));
          });

          return {
            editionMaxMintablePromise,
            editionTotalMintedPromise,
            tierInfo,
          };
        },
      });

      const mintedByUserFromSchedule = LazyPromise(async () => {
        assertAddress(userAddress);

        assertAddress(editionAddress);

        return BigInt(
          await tieredProvider(chainId).readContract({
            abi: SUPER_MINTER_ABI,
            address: getSoundContractAddressesConstants(chainId).superMinterV1,
            functionName: 'numberMinted',
            args: [editionAddress, mintSchedule.tier, mintSchedule.scheduleNum, userAddress],
          }),
        );
      });

      return hasAccessToTieredDrop({
        ...scheduleAccessDropInfo,
        userAddress,
        editionMaxMintablePromise: LazyPromise(() =>
          sharedPromises.then(v => v.editionMaxMintablePromise),
        ),
        mintedByUserFromSchedule,
        editionTotalMintedPromise: LazyPromise(() =>
          sharedPromises.then(v => v.editionTotalMintedPromise),
        ),
      });
    },
  } satisfies UseQueryOptions;
}

export async function hasAccessToTieredDrop({
  scheduleStartTime,
  scheduleEndTime,
  scheduleMerkleInfo,
  scheduleMintPaused,
  scheduleRangeInfo,
  scheduleTotalMinted,
  scheduleMaxMintable,
  scheduleMaxMintablePerAccount,
  userAddress,
  editionTotalMintedPromise,
  editionMaxMintablePromise,
  mintedByUserFromSchedule,
}: AccessToDropScheduleInfo & {
  userAddress: string;

  editionTotalMintedPromise: Promise<bigint>;
  editionMaxMintablePromise: Promise<number>;
  mintedByUserFromSchedule: Promise<bigint>;
}): Promise<boolean> {
  // check valid mint time
  if (scheduleStartTime === scheduleEndTime || scheduleMintPaused) {
    return false;
  }

  // Checks for child minter custom logic
  if (scheduleRangeInfo) {
    const maxQty =
      scheduleStartTime < scheduleRangeInfo.cutoffTime
        ? scheduleRangeInfo.maxMintableUpper
        : scheduleRangeInfo.maxMintableLower;

    if (scheduleTotalMinted >= maxQty) {
      return false;
    }
  } else if (scheduleMerkleInfo) {
    const proof = await sdkSoundAPI.merkleProof({
      merkleRoot: scheduleMerkleInfo.merkleRoot,
      userAddress,
    });

    if (!proof?.length) {
      return false;
    }
  }

  const [editionTotalMinted, editionMaxMintable] = await Promise.all([
    editionTotalMintedPromise,
    editionMaxMintablePromise,
  ]);

  const editionRemainingQty =
    editionMaxMintable - bigIntToNumber(editionTotalMinted, { strict: false });

  if (!editionRemainingQty) {
    return false;
  }

  // Get eligible quantity for the user on this mint schedule.
  const remainingForSchedule = scheduleMaxMintable - scheduleTotalMinted;

  const eligibleForUserOnSchedule =
    scheduleMaxMintablePerAccount - Number(await mintedByUserFromSchedule);
  const scheduleEligibleQty = Math.min(remainingForSchedule, eligibleForUserOnSchedule);

  // Return the minimum of the two. The number of tokens minted within the mint schedule
  // can never exceed the number of tokens available for the edition.
  return Math.max(0, Math.min(scheduleEligibleQty, editionRemainingQty)) !== 0;
}

function tieredEligibleQuantityQueryKey({
  mintSchedule,
  userAddress,
  editionAddress,
}: {
  mintSchedule: Maybe<SuperMinterScheduleWithTier>;
  userAddress: Maybe<string>;
  editionAddress: Maybe<string>;
}): QueryKey {
  return [
    SDK_KEY,
    'tiered-edition-eligible-quantity',
    editionAddress,
    mintSchedule?.tier,
    mintSchedule?.scheduleNum,
    userAddress,
  ] as const;
}

function tieredEditionInfoQueryKey({
  editionAddress,
  chainId,
}: {
  editionAddress: string;
  chainId: number;
}) {
  return [SDK_KEY, 'tiered-info', editionAddress, chainId] as const;
}

function tieredEditionInfoQueryFn({
  editionAddress,
  chainId,
  tieredProvider,
}: {
  editionAddress: string;
  chainId: number;
  tieredProvider: ReturnType<typeof useTieredSdk>['tieredProvider'];
}) {
  assertAddress(editionAddress);
  return tieredProvider(chainId).getEditionContractInfo({
    edition: editionAddress,
  });
}
